// @flow
import * as React from 'react';
import { compose, withState, withHandlers } from 'recompose';
import Radium from 'radium';
import { connect } from 'react-redux';
import uuid from 'uuid';
import {
  Card,
  CardHeader,
  CardBlock,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';

import { createTopic, loadTopics } from 'actions/topics';

import OptionsSelector from 'components/OptionsSelector';
import ImageUpload from 'components/ImageUpload';

import { Spacing } from 'utils/styleguide';

type Props = {
  // recompose
  title: string,
  onTitleChange: (title: string) => void,
  description: string,
  onDescriptionChange: (description: string) => void,
  visibleIn: Array<string>,
  onVisibleInChange: (communityIds: Array<string>) => void,
  images: { [imageId: string]: string },
  onImagesChange: (images: { [imageId: string]: string }) => void,
  setImage: (id: string, data: string) => void,

  // redux
  communities: { [communityId: string]: string },
  error: ?string,
  onCreateTopic: typeof createTopic,
  onLoadTopics: typeof loadTopics,
};

class NewTopic extends React.Component<Props> {
  _onSubmitTopic = async () => {
    const {
      title,
      onTitleChange,
      description,
      onDescriptionChange,
      visibleIn,
      onVisibleInChange,
      onCreateTopic,
      onLoadTopics,
      images,
      onImagesChange,
    } = this.props;

    await onCreateTopic(title, description, Object.values(images), visibleIn);
    await onLoadTopics();

    onTitleChange('');
    onDescriptionChange('');
    onVisibleInChange([]);
    onImagesChange({});
  };

  render() {
    const {
      title,
      onTitleChange,
      description,
      onDescriptionChange,
      communities,
      onVisibleInChange,
      images,
      setImage,
      error,
    } = this.props;

    return (
      <Card style={styles.root}>
        <CardHeader style={styles.title}>Neue Meldung erstellen</CardHeader>
        <CardBlock>
          <FormGroup>
            <Input
              type="textfield"
              placeholder="Titel der Meldung ..."
              value={title}
              onChange={event => onTitleChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Input
              type="textarea"
              placeholder="Beschreibung der Meldung ..."
              value={description}
              onChange={event => onDescriptionChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Bilder hinzufügen</Label>
            <div style={styles.images}>
              {Object.keys(images).map(imageId => {
                return (
                  <div key={imageId} style={{ marginRight: Spacing.Micro }}>
                    <ImageUpload
                      image={images[imageId]}
                      onImageSelected={data => {
                        setImage(imageId, data);
                      }}
                    />
                  </div>
                );
              })}
              {Object.keys(images).length < 4 && (
                <div style={{ marginRight: Spacing.Micro }}>
                  <ImageUpload
                    onImageSelected={data => setImage(uuid.v4(), data)}
                  />
                </div>
              )}
            </div>
          </FormGroup>
          <OptionsSelector
            label="Auswahl der Gemeinden"
            options={communities}
            onOptionsSelected={onVisibleInChange}
          />
          {!!error && (
            <Alert color="danger">
              {error}: Konnte Meldung nicht anlegen. Bitte Support kontaktieren.
            </Alert>
          )}
          <Button
            color="primary"
            disabled={!title || !description}
            onClick={this._onSubmitTopic}
          >
            Meldung erstellen
          </Button>
        </CardBlock>
      </Card>
    );
  }
}

const styles = {
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
  },
  images: {
    display: 'flex',
  },
};

const mapStateToProps = ({
  auth: {
    user: { communities },
  },
  topics: { error },
}) => ({
  communities,
  error: (error && error.code) || null,
});
const mapDispatchToProps = dispatch => ({
  onCreateTopic: (...args) => dispatch(createTopic(...args)),
  onLoadTopics: (...args) => dispatch(loadTopics(...args)),
});

const NewTopicContainer = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withState('title', 'onTitleChange', ''),
  withState('description', 'onDescriptionChange', ''),
  withState('visibleIn', 'onVisibleInChange', []),
  withState('images', 'onImagesChange', {}),
  withHandlers({
    setImage: ({ images, onImagesChange }) => (id: string, data: string) => {
      if (!!data) {
        onImagesChange({ ...images, [id]: data });
      } else {
        const newImages = { ...images };
        delete newImages[id];
        onImagesChange(newImages);
      }
    },
  }),
  Radium
)(NewTopic);

export default NewTopicContainer;



// WEBPACK FOOTER //
// ./src/views/Topics/NewTopic.js